/**
 * Coluna da esquerda do Assistente: as conversas salvas de quem pergunta
 * (cada um vê só as suas), a mais nova em cima, e "Nova conversa".
 *
 * Cada linha abre a conversa; renomear e apagar ficam ao lado, discretos até
 * o mouse ou o foco chegarem (no toque ficam sempre, bem leves). Renomear
 * troca o título por um campo: Enter salva, ESC desiste.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiEdit, FiEdit2, FiTrash2 } from "react-icons/fi";
import styled from "styled-components";

import { plural } from "../../../utils/dinheiro";
import { horaNaLista } from "../apresentacao";
import { ErroDaCentral } from "../comum";
import {
  AvisoDaLista,
  BotaoIcone,
  ColunaLista,
  ItensDaLista,
  LinhaDoTitulo,
  Osso,
  Rolagem,
  TopoDaLista,
} from "../conversas/estilos";

const TAMANHO_MAXIMO_DO_TITULO = 120;
const LINHAS_DO_ESQUELETO = 5;

const ColunaDoAssistente = styled(ColunaLista)`
  flex-basis: clamp(260px, 28%, 320px);
  width: clamp(260px, 28%, 320px);
`;

const Linha = styled.li`
  position: relative;
  margin: 0 8px;

  & + & {
    margin-top: 2px;
  }
`;

const ItemDaConversa = styled.button`
  display: grid;
  gap: 2px;
  width: 100%;
  min-height: 56px;
  /* Folga à direita para os dois botões da linha. */
  padding: 9px 76px 9px 12px;
  border: 0;
  border-radius: 10px;
  background: ${({ $ativa }) => ($ativa ? "var(--surface-3)" : "transparent")};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $ativa }) => ($ativa ? "var(--surface-3)" : "var(--surface-2)")};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  b {
    min-width: 0;
    font-size: 14px;
    font-weight: ${({ $ativa }) => ($ativa ? 600 : 500)};
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    font-size: 12px;
    color: var(--ink-3);
    font-variant-numeric: tabular-nums;
  }
`;

const AcoesDaLinha = styled.span`
  position: absolute;
  right: 6px;
  top: 50%;
  display: flex;
  gap: 2px;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 120ms ease-out;

  ${Linha}:hover &,
  ${Linha}:focus-within &,
  ${Linha}[data-ativa="sim"] & {
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 0.7;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const AcaoDaLinha = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;

  &:hover {
    background: var(--surface);
    color: var(--ink);
  }

  &[data-perigo="sim"]:hover {
    color: var(--danger-text);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
`;

const CampoDoTitulo = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  padding: 0 12px;
  border: 1px solid var(--accent);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 14px;
  box-shadow: var(--shadow-focus);
  outline: none;
`;

const ListaDasConversas = styled(ItensDaLista)`
  padding: 8px 0;
`;

const EsqueletoDaLinha = styled.li`
  display: grid;
  gap: 8px;
  padding: 14px 20px;
`;

function Esqueleto() {
  return (
    <ItensDaLista aria-hidden="true">
      {Array.from({ length: LINHAS_DO_ESQUELETO }, (_, i) => (
        <EsqueletoDaLinha key={i}>
          <Osso $altura={11} $largura={`${70 - i * 6}%`} />
          <Osso $altura={9} $largura="35%" />
        </EsqueletoDaLinha>
      ))}
    </ItensDaLista>
  );
}

function TituloEmEdicao({ titulo, aoSalvar, aoDesistir }) {
  const [valor, setValor] = useState(titulo);
  const campo = useRef(null);
  const resolvido = useRef(false);
  useEffect(() => {
    campo.current?.focus();
    campo.current?.select();
  }, []);

  function terminar(salvar) {
    if (resolvido.current) return;
    resolvido.current = true;
    const limpo = valor.trim();
    if (salvar && limpo && limpo !== titulo) aoSalvar(limpo);
    else aoDesistir();
  }

  return (
    <CampoDoTitulo
      ref={campo}
      value={valor}
      maxLength={TAMANHO_MAXIMO_DO_TITULO}
      aria-label="Novo nome da conversa"
      onChange={(e) => setValor(e.target.value)}
      onBlur={() => terminar(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          terminar(true);
        } else if (e.key === "Escape") {
          // O ESC é do campo: a conversa aberta não fecha junto.
          e.preventDefault();
          e.stopPropagation();
          terminar(false);
        }
      }}
    />
  );
}

function LinhaDaConversa({ conversa, ativa, aoAbrir, aoRenomear, aoApagar }) {
  const [renomeando, setRenomeando] = useState(false);
  const titulo = conversa.titulo || "Conversa sem título";
  if (renomeando) {
    return (
      <Linha>
        <TituloEmEdicao
          titulo={conversa.titulo || ""}
          aoSalvar={(novo) => {
            setRenomeando(false);
            aoRenomear(conversa, novo);
          }}
          aoDesistir={() => setRenomeando(false)}
        />
      </Linha>
    );
  }
  return (
    <Linha data-ativa={ativa ? "sim" : undefined}>
      <ItemDaConversa
        type="button"
        $ativa={ativa}
        aria-current={ativa ? "page" : undefined}
        data-conversa-assistente-id={conversa.id}
        title={titulo}
        onClick={() => aoAbrir(conversa)}
      >
        <b>{titulo}</b>
        <small>
          {plural(Number(conversa.perguntas) || 0, "pergunta", "perguntas")} · {horaNaLista(conversa.atualizada_em)}
        </small>
      </ItemDaConversa>
      <AcoesDaLinha>
        <AcaoDaLinha type="button" aria-label={`Renomear a conversa ${titulo}`} title="Renomear" onClick={() => setRenomeando(true)}>
          <FiEdit2 size={14} aria-hidden="true" />
        </AcaoDaLinha>
        <AcaoDaLinha
          type="button"
          data-perigo="sim"
          aria-label={`Apagar a conversa ${titulo}`}
          title="Apagar"
          onClick={() => aoApagar(conversa)}
        >
          <FiTrash2 size={14} aria-hidden="true" />
        </AcaoDaLinha>
      </AcoesDaLinha>
    </Linha>
  );
}

export default function ListaDoAssistente({
  conversas,
  carregando,
  erro,
  ativaId,
  escondida = false,
  aoVoltar,
  aoNova,
  aoAbrir,
  aoRenomear,
  aoApagar,
}) {
  return (
    <ColunaDoAssistente aria-label="Conversas com o assistente" hidden={escondida}>
      <TopoDaLista>
        <LinhaDoTitulo>
          <BotaoIcone type="button" aria-label="Voltar ao painel da Central" title="Voltar ao painel" onClick={aoVoltar}>
            <FiArrowLeft size={18} aria-hidden="true" />
          </BotaoIcone>
          <h1>Assistente</h1>
          <BotaoIcone type="button" aria-label="Nova conversa" title="Nova conversa" onClick={aoNova}>
            <FiEdit size={17} aria-hidden="true" />
          </BotaoIcone>
        </LinhaDoTitulo>
      </TopoDaLista>
      <Rolagem>
        {erro ? <div style={{ padding: 12 }}><ErroDaCentral erro={erro} /></div> : null}
        {!erro && carregando && !conversas ? <Esqueleto /> : null}
        {conversas && conversas.length === 0 ? (
          <AvisoDaLista>Nenhuma conversa salva ainda. Cada pergunta nova começa uma conversa aqui, só para você.</AvisoDaLista>
        ) : null}
        {conversas && conversas.length > 0 ? (
          <ListaDasConversas>
            {conversas.map((c) => (
              <LinhaDaConversa
                key={c.id}
                conversa={c}
                ativa={String(c.id) === String(ativaId)}
                aoAbrir={aoAbrir}
                aoRenomear={aoRenomear}
                aoApagar={aoApagar}
              />
            ))}
          </ListaDasConversas>
        ) : null}
      </Rolagem>
    </ColunaDoAssistente>
  );
}
